import { useEffect, useRef } from 'react'
import EmitterService from '~/services/EventEmitter/EmitterService'
import {
  EmitterEventTypes,
  EmitterSubscription,
  EmitterUnsubscribe,
} from '~/services/EventEmitter/types/EmitterTypeDefs'
import Logger from '~/services/Logger'

interface UseEmitterServiceSubscribe<TEventType extends EmitterEventTypes> {
  subscription: EmitterSubscription<TEventType>
  subscriptionToken: string
  singleSubscription?: boolean
}

// This maintains a list of unsubscribe functions for each subscription token.
const globalSubscriptions = new Map<string, EmitterUnsubscribe[]>()
const logger = new Logger('useEmitterService')

export const useEmitterService = () => {
  // This ref will keep track of local unsubscribe functions for this hook instance.
  const localUnsubscribesRef = useRef<EmitterUnsubscribe[]>([])

  function subscribe<TEventType extends EmitterEventTypes>({
    subscription,
    subscriptionToken,
    singleSubscription = true,
  }: UseEmitterServiceSubscribe<TEventType>) {
    if (!subscriptionToken) {
      logger.debug(
        `No subscription token provided - skipping ${subscription.eventType} subscription.`,
      )
      return
    }

    if (singleSubscription) {
      // Unsubscribe and clear the existing subscriptions for this token.
      const existingUnsubscribes = globalSubscriptions.get(subscriptionToken)
      existingUnsubscribes?.forEach((unsub) => unsub())
      globalSubscriptions.set(subscriptionToken, [])
    }

    // Subscribe to the event.
    const unsubscribe = EmitterService.subscribe(subscription)

    // Update the references in both global and local containers.
    const currentGlobalUnsubscribes =
      globalSubscriptions.get(subscriptionToken) ?? []
    currentGlobalUnsubscribes.push(unsubscribe)
    globalSubscriptions.set(subscriptionToken, currentGlobalUnsubscribes)

    localUnsubscribesRef.current.push(unsubscribe)
  }

  const emit = EmitterService.emit

  // On unmount, unsubscribe only from the local subscriptions.
  useEffect(() => {
    return () => {
      localUnsubscribesRef.current.forEach((localUnsubscribe) => {
        localUnsubscribe()
        // Here, we need to ensure we also remove this specific function from the globalSubscriptions.
        for (const [token, unsubscribes] of globalSubscriptions.entries()) {
          const index = unsubscribes.indexOf(localUnsubscribe)
          if (index !== -1) {
            unsubscribes.splice(index, 1)
            // If there are no more unsubscribes for this token, remove it from the global map.
            if (unsubscribes.length === 0) {
              globalSubscriptions.delete(token)
            }
          }
        }
      })
      localUnsubscribesRef.current = []
    }
  }, [])

  return {
    subscribe,
    emit,
  }
}
